import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Switch, Input, Modal, message, Typography, Empty, Result,
  Descriptions, Space, Tooltip, Card, Row, Col, Select, Spin, Divider,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, FileTextOutlined, FileTextFilled,
  WarningOutlined, SaveOutlined, LinkOutlined, CheckCircleFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getFarmacias,
  updateFarmaciaEstado,
  getGescomFarmacia,
  vincularFarmacia,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  type ReconciliacionFarmacia,
  type GescomFarmaciaData,
} from '../../api/reconciliacionApi';

const { Text } = Typography;
const { TextArea } = Input;

const estadoLabels: Record<number, { label: string; color: string }> = {
  0: { label: 'Pendiente', color: 'orange' },
  1: { label: 'Realizado', color: 'green' },
  2: { label: 'Descartado', color: 'default' },
};

interface FarmaciasTabProps {
  selectedRecIds?: number[];
}

const FarmaciasTab: React.FC<FarmaciasTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionFarmacia[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionFarmacia[]>([]);
  const [showRealizados, setShowRealizados] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionFarmacia | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [actionLoading, setActionLoading] = useState(false);

  // Gescom farmacia lookup
  const [gescomIdInput, setGescomIdInput] = useState('');
  const [gescomData, setGescomData] = useState<GescomFarmaciaData | null>(null);
  const [gescomLoading, setGescomLoading] = useState(false);
  const [gescomError, setGescomError] = useState<string | null>(null);

  // Vinculación
  const [vinculando, setVinculando] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const estadoFilter = showRealizados ? undefined : ESTADO_PENDIENTE;
      let result: ReconciliacionFarmacia[];
      if (!selectedRecIds || selectedRecIds.length === 0) {
        result = await getFarmacias(undefined, estadoFilter);
      } else {
        const results = await Promise.all(selectedRecIds.map(rid => getFarmacias(rid, estadoFilter)));
        result = results.flat();
      }
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [showRealizados, selectedRecIds]);

  const applySearch = (items: ReconciliacionFarmacia[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(f =>
      (f.farmaciaGescom?._Name || '').toLowerCase().includes(lower) ||
      (f.cif || '').toLowerCase().includes(lower) ||
      (f.direccion || '').toLowerCase().includes(lower) ||
      (f.localidad || '').toLowerCase().includes(lower) ||
      (f.provincia || '').toLowerCase().includes(lower) ||
      (f.licenciaSOE || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateFarmaciaEstado(id, estadoId, notasGestion);
      const label = estadoLabels[estadoId]?.label || 'actualizada';
      message.success(`Farmacia ${label.toLowerCase()} correctamente`);
      setModalVisible(false);
      loadData();
    } catch {
      message.error('Error al actualizar la farmacia');
    } finally {
      setActionLoading(false);
    }
  };

  // Cargar datos de Gescom para una farmacia
  const loadGescomData = async (farmaciaGsId: number) => {
    setGescomLoading(true);
    setGescomData(null);
    setGescomError(null);
    try {
      const data = await getGescomFarmacia(farmaciaGsId);
      setGescomData(data);
    } catch {
      setGescomError('No se encontró la farmacia en Gescom o error de conexión');
    } finally {
      setGescomLoading(false);
    }
  };

  // Buscar farmacia en Gescom por ID introducido manualmente
  const handleBuscarGescom = () => {
    const id = parseInt(gescomIdInput, 10);
    if (isNaN(id) || id <= 0) {
      setGescomError('Introduce un ID numérico válido');
      return;
    }
    loadGescomData(id);
  };

  // Vincular farmacia CU2 con Gescom
  const handleVincular = async () => {
    if (!selectedItem || !gescomData) return;
    setVinculando(true);
    try {
      const result = await vincularFarmacia(selectedItem.id, gescomData.id);
      const code = parseInt(result.code, 10);

      if (code < 0) {
        message.error(result.message);
        return;
      }

      // Éxito: refrescar datos Gescom para verificar vinculación
      try {
        const refreshed = await getGescomFarmacia(gescomData.id);
        if (refreshed.idFarmaciaCU2 !== selectedItem.idFarmaciaCU2) {
          message.error(`Verificación fallida: id_farmacia_CU2 en Gescom (${refreshed.idFarmaciaCU2}) no coincide con id_farmacia de CU2 (${selectedItem.idFarmaciaCU2}).`);
          setGescomData(refreshed);
          return;
        }
        setGescomData(refreshed);
      } catch {
        // Si falla la recarga, no bloqueamos
      }

      // Actualizar el item local para reflejar la vinculación sin cerrar el modal
      const updated = { ...selectedItem, idFarmaciaGS: gescomData.id };
      setSelectedItem(updated);
      setData(prev => prev.map(f => f.id === updated.id ? updated : f));
      setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al vincular los registros';
      message.error(msg);
    } finally {
      setVinculando(false);
    }
  };

  const openDetail = (item: ReconciliacionFarmacia) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setGescomIdInput('');
    setGescomData(null);
    setGescomError(null);
    setModalVisible(true);

    // Si es ALTA y ya tiene idFarmaciaGS, cargar automáticamente datos Gescom
    if (item.tipoOperacion === 'ALTA' && item.idFarmaciaGS) {
      loadGescomData(item.idFarmaciaGS);
    }
  };

  const pendingCount = data.filter(f => f.estadoId === ESTADO_PENDIENTE).length;

  const columns: ColumnsType<ReconciliacionFarmacia> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoOperacion',
      key: 'tipo',
      width: 80,
      filters: [
        { text: 'ALTA', value: 'ALTA' },
        { text: 'BAJA', value: 'BAJA' },
      ],
      onFilter: (value, record) => record.tipoOperacion === value,
      render: (val: string) => <Tag color={val === 'ALTA' ? 'green' : 'red'}>{val}</Tag>,
    },
    {
      title: 'Farmacia',
      key: 'nombre',
      render: (_, r) => r.farmaciaGescom?._Name || r.direccion || `CU2 ID ${r.idFarmaciaCU2 || r.idFarmaciaGS}`,
      ellipsis: true,
      width: 280,
    },
    {
      title: 'CIF',
      dataIndex: 'cif',
      key: 'cif',
      width: 110,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 130,
    },
    {
      title: 'Localidad',
      dataIndex: 'localidad',
      key: 'localidad',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      width: 110,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Relaciones',
      key: 'relaciones',
      width: 90,
      render: (_, r) => r.relacionesPendientes ? (
        <Tooltip title="Tiene titularidades/empleados pendientes"><Tag color="volcano" icon={<WarningOutlined />}>Si</Tag></Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number) => {
        const est = estadoLabels[val] || { label: `Estado ${val}`, color: 'default' };
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextFilled style={{ color: '#1890ff' }} /></Tooltip>
      ) : (
        <FileTextOutlined style={{ color: '#d9d9d9' }} />
      ),
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar farmacias"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  // Render del modal ALTA con comparación CU2/Gescom
  const renderAltaModal = (item: ReconciliacionFarmacia) => (
    <div>
      {/* Relaciones pendientes — aviso superior */}
      {item.relacionesPendientes && (
        <div style={{ background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 6, padding: 12, marginBottom: 16 }}>
          <WarningOutlined style={{ color: '#d4380d', marginRight: 8 }} />
          <Text strong style={{ color: '#d4380d' }}>Titularidades / empleados pendientes de esta farmacia</Text>
          <div style={{ marginTop: 4 }}>
            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.relacionesPendientes}</Text>
          </div>
        </div>
      )}

      {/* Dos columnas: CU2 (izq) | Gescom (der) */}
      <Row gutter={16}>
        {/* ─── Columna CU2 ─── */}
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: '#1890ff' }}>Datos CU2</span>}
            size="small"
            style={{ marginBottom: 16 }}
            headStyle={{ background: '#e6f7ff', borderBottom: '1px solid #91d5ff' }}
          >
            <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: '#595959' }}>
              <Descriptions.Item label="id_farmacia">
                <Text code>{item.idFarmaciaCU2 ?? '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="id_farmacia_gs">
                {item.idFarmaciaGS
                  ? <Text code>{item.idFarmaciaGS}</Text>
                  : <Text type="secondary">sin vincular</Text>
                }
              </Descriptions.Item>
              <Descriptions.Item label="Direccion">{item.direccion || '—'}</Descriptions.Item>
              <Descriptions.Item label="Poblacion">{item.localidad || '—'}</Descriptions.Item>
              <Descriptions.Item label="C.Postal">{item.cp || '—'}</Descriptions.Item>
              <Descriptions.Item label="Provincia">{item.provincia || '—'}</Descriptions.Item>
              <Descriptions.Item label="Licencia SOE">{item.licenciaSOE || '—'}</Descriptions.Item>
              <Descriptions.Item label="Telefono">{item.telefono || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* ─── Columna Gescom ─── */}
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: '#389e0d' }}>Datos Gescom</span>}
            size="small"
            style={{ marginBottom: 16, borderColor: '#b7eb8f' }}
            headStyle={{ backgroundColor: '#f6ffed', borderBottom: '1px solid #b7eb8f', fontWeight: 'bold', color: '#389e0d' }}
          >
            <Spin spinning={gescomLoading}>
              <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 130, color: '#595959' }}>
                <Descriptions.Item label="id_farmacia_CU2">
                  {gescomData?.idFarmaciaCU2
                    ? <Text code>{gescomData.idFarmaciaCU2}</Text>
                    : <Text type="secondary">{gescomData ? 'sin asignar' : '—'}</Text>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="ID">
                  {item.idFarmaciaGS ? (
                    <>
                      <Text code>{item.idFarmaciaGS}</Text>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => loadGescomData(item.idFarmaciaGS!)}
                        loading={gescomLoading}
                        style={{ marginLeft: 8 }}
                      />
                    </>
                  ) : (
                    <>
                      <Space.Compact size="small">
                        <Input
                          placeholder="ID"
                          value={gescomData ? String(gescomData.id) : gescomIdInput}
                          onChange={e => { setGescomIdInput(e.target.value); setGescomData(null); }}
                          onPressEnter={handleBuscarGescom}
                          style={{ width: 100 }}
                          disabled={!!gescomData}
                        />
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => gescomData ? loadGescomData(gescomData.id) : handleBuscarGescom()}
                          loading={gescomLoading}
                          size="small"
                        />
                      </Space.Compact>
                      {gescomError && (
                        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{gescomError}</Text>
                      )}
                    </>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Direccion">{gescomData?.direccion || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="Poblacion">{gescomData?.localidad || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="C.Postal">{gescomData?.cp || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="Provincia">{gescomData?.provincia || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="Licencia SOE">{gescomData?.licenciaSOE != null ? String(gescomData.licenciaSOE) : <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="Telefono">{gescomData?.telefono || <Text type="secondary">—</Text>}</Descriptions.Item>
              </Descriptions>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Zona de vinculación */}
      {item.idFarmaciaGS ? (
        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, marginBottom: 16, textAlign: 'center' }}>
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16, marginRight: 8 }} />
          <Text strong style={{ color: '#389e0d' }}>Registros vinculados</Text>
        </div>
      ) : gescomData && item.estadoId === ESTADO_PENDIENTE ? (
        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 16, marginBottom: 16, textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={<LinkOutlined />}
            loading={vinculando}
            onClick={() => {
              Modal.confirm({
                width: 520,
                title: 'Vincular registros',
                content: (
                  <div>
                    <p>Se vinculará la farmacia de CU2 con el registro de Gescom:</p>
                    <ul style={{ margin: '8px 0' }}>
                      <li><strong>CU2:</strong> id_farmacia {item.idFarmaciaCU2} — {item.direccion || item.localidad}</li>
                      <li><strong>Gescom:</strong> ID {gescomData.id} — {gescomData.direccion || gescomData.localidad}</li>
                    </ul>
                    <p>La vinculación se registrará en ambas bases de datos. ¿Continuar?</p>
                  </div>
                ),
                okText: 'Vincular',
                cancelText: 'Cancelar',
                onOk: handleVincular,
              });
            }}
          >
            Vincular registros
          </Button>
        </div>
      ) : null}

      <Divider style={{ margin: '12px 0' }} />

      {/* Comentario */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
      <TextArea
        value={editNotas}
        onChange={e => setEditNotas(e.target.value)}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder="Anadir comentario..."
        style={{ marginBottom: 16 }}
      />

      {/* Acciones: estado selector + Guardar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          value={editEstadoId}
          onChange={setEditEstadoId}
          style={{ width: 160 }}
        >
          <Select.Option value={ESTADO_PENDIENTE}><span style={{ color: '#fa8c16' }}>● Pendiente</span></Select.Option>
          <Select.Option value={ESTADO_REALIZADO}><span style={{ color: '#52c41a' }}>● Realizado</span></Select.Option>
          <Select.Option value={ESTADO_DESCARTADO}><span style={{ color: '#8c8c8c' }}>● Descartado</span></Select.Option>
        </Select>

        <Button
          icon={<SaveOutlined />}
          onClick={() => handleUpdateEstado(item.id, editEstadoId, editNotas || undefined)}
          loading={actionLoading}
        >
          Guardar
        </Button>
      </div>
    </div>
  );

  // Render del modal BAJA (mantiene el formato existente)
  const renderBajaModal = (item: ReconciliacionFarmacia) => (
    <div>
      {/* Relaciones pendientes — aviso superior */}
      {item.relacionesPendientes && (
        <div style={{ background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 6, padding: 12, marginBottom: 16 }}>
          <WarningOutlined style={{ color: '#d4380d', marginRight: 8 }} />
          <Text strong style={{ color: '#d4380d' }}>Titularidades / empleados pendientes de esta farmacia</Text>
          <div style={{ marginTop: 4 }}>
            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.relacionesPendientes}</Text>
          </div>
        </div>
      )}

      {/* Datos del caso (CU2) */}
      <Descriptions title="Datos de CU2" bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Licencia SOE">{item.licenciaSOE || '-'}</Descriptions.Item>
        <Descriptions.Item label="CIF">{item.cif || '-'}</Descriptions.Item>
        <Descriptions.Item label="Telefono">{item.telefono || '-'}</Descriptions.Item>
        <Descriptions.Item label="Codigo postal">{item.cp || '-'}</Descriptions.Item>
        <Descriptions.Item label="Direccion" span={2}>{item.direccion || '-'}</Descriptions.Item>
        <Descriptions.Item label="Localidad">{item.localidad || '-'}</Descriptions.Item>
        <Descriptions.Item label="Provincia">{item.provincia || '-'}</Descriptions.Item>
        {item.farmaciaGescom && (
          <Descriptions.Item label="Farmacia Gescom" span={2}>
            {item.farmaciaGescom._Name} (ID: {item.idFarmaciaGS})
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Accion */}
      {item.estadoId === ESTADO_PENDIENTE && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <Text strong>Accion: confirmar baja tras gestion manual en Gescom</Text>
          <div style={{ marginTop: 12 }}>
            <Button
              type="primary"
              loading={actionLoading}
              onClick={() => {
                Modal.confirm({
                  title: 'Confirmar baja',
                  content: 'Confirma que has aplicado la baja en Gescom.',
                  okText: 'Confirmar',
                  cancelText: 'Cancelar',
                  onOk: () => handleUpdateEstado(item.id, ESTADO_REALIZADO, editNotas || undefined),
                });
              }}
            >
              Confirmar baja
            </Button>
          </div>
        </div>
      )}

      {/* Comentario */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
      <TextArea
        value={editNotas}
        onChange={e => setEditNotas(e.target.value)}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder="Anadir comentario..."
        disabled={item.estadoId !== ESTADO_PENDIENTE}
        style={{ marginBottom: 16 }}
      />
      {item.estadoId === ESTADO_PENDIENTE && editNotas !== (item.notasGestion || '') && (
        <Button onClick={() => handleUpdateEstado(item.id, item.estadoId, editNotas || undefined)} loading={actionLoading}>
          Guardar comentario
        </Button>
      )}
    </div>
  );

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Text strong>{pendingCount} pendientes</Text>
        <div style={{ flex: 1 }} />
        <Switch
          checked={showRealizados}
          onChange={setShowRealizados}
          checkedChildren="Todos"
          unCheckedChildren="Solo pendientes"
        />
        <Input
          placeholder="Buscar por nombre, CIF..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={loadData}>Refrescar</Button>
      </div>

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(f => ({ ...f, key: f.id }))}
        columns={columns}
        loading={loading}
        pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        size="middle"
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="No hay farmacias pendientes de conciliacion" /> }}
      />

      {/* Modal de detalle */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? `${selectedItem.tipoOperacion === 'ALTA' ? 'Alta' : 'Baja'} de farmacia` : ''}
        width={selectedItem?.tipoOperacion === 'ALTA' ? 900 : 700}
        destroyOnClose
      >
        {selectedItem && (
          selectedItem.tipoOperacion === 'ALTA'
            ? renderAltaModal(selectedItem)
            : renderBajaModal(selectedItem)
        )}
      </Modal>
    </div>
  );
};

export default FarmaciasTab;
